import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, User, Menu, X, Heart, Search, LogOut, Package, Settings, ChevronDown, UtensilsCrossed, Palette, Sparkles } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { useSite } from '../context/SiteContext';
import { Button } from '../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

// Configurazione macro types per il menu
const MACRO_TYPES = [
  { id: 'FOOD', label: 'Food', icon: UtensilsCrossed, color: 'text-orange-600' },
  { id: 'ARTIGIANATO', label: 'Artigianato', icon: Palette, color: 'text-blue-600' },
  { id: 'EXPERIENCE', label: 'Experience', icon: Sparkles, color: 'text-purple-600' }
];

const Header = () => {
  const { user, logout, isAuthenticated } = useAuth();
  const { itemCount } = useCart();
  const { logo_url, platform_name } = useSite();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [searchOpen, setSearchOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState('');

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/prodotti?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchOpen(false);
      setSearchQuery('');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <header className="sticky top-0 z-50 bg-white border-b border-border shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            {logo_url ? (
              <img
                src={logo_url}
                alt={platform_name}
                className="h-10 md:h-12 w-auto object-contain"
              />
            ) : (
              <span className="font-heading text-2xl md:text-3xl font-bold text-primary tracking-tight">
                {platform_name}
              </span>
            )}
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            {/* Prodotti Dropdown Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-1 text-text-secondary hover:text-primary transition-colors font-medium outline-none" data-testid="nav-products-dropdown">
                Prodotti
                <ChevronDown className="w-4 h-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-48">
                <DropdownMenuItem asChild>
                  <Link to="/prodotti" className="flex items-center gap-2 cursor-pointer" data-testid="nav-products-all">
                    Tutti i Prodotti
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                {MACRO_TYPES.map(macro => {
                  const Icon = macro.icon;
                  return (
                    <DropdownMenuItem key={macro.id} asChild>
                      <Link 
                        to={`/prodotti?macro_type=${macro.id}`} 
                        className={`flex items-center gap-2 cursor-pointer`}
                        data-testid={`nav-products-${macro.id.toLowerCase()}`}
                      >
                        <Icon className={`w-4 h-4 ${macro.color}`} />
                        {macro.label}
                      </Link>
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
            <Link to="/produttori" className="text-text-secondary hover:text-primary transition-colors font-medium" data-testid="nav-producers">
              Produttori
            </Link>
            <Link to="/regioni" className="text-text-secondary hover:text-primary transition-colors font-medium" data-testid="nav-regions">
              Regioni
            </Link>
          </nav>

          {/* Desktop Actions */}
          <div className="hidden md:flex items-center gap-4">
            <button 
              onClick={() => setSearchOpen(!searchOpen)} 
              className="p-2 hover:bg-background rounded-lg transition-colors"
              data-testid="search-toggle"
            >
              <Search className="w-5 h-5 text-text-secondary" />
            </button>

            {isAuthenticated && (
              <Link to="/preferiti" className="p-2 hover:bg-background rounded-lg transition-colors" data-testid="favorites-link">
                <Heart className="w-5 h-5 text-text-secondary" />
              </Link>
            )}

            <Link to="/carrello" className="relative p-2 hover:bg-background rounded-lg transition-colors" data-testid="cart-link">
              <ShoppingCart className="w-5 h-5 text-text-secondary" />
              {itemCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-primary text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-medium">
                  {itemCount}
                </span>
              )}
            </Link>

            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="gap-2" data-testid="user-menu-trigger">
                    <User className="w-5 h-5" />
                    <span className="max-w-24 truncate">{user?.first_name || user?.email}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem onClick={() => navigate('/account')} data-testid="menu-account">
                    <User className="w-4 h-4 mr-2" />
                    Il mio account
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/ordini')} data-testid="menu-orders">
                    <Package className="w-4 h-4 mr-2" />
                    I miei ordini
                  </DropdownMenuItem>
                  {user?.role === 'producer' && (
                    <DropdownMenuItem onClick={() => navigate('/produttore/dashboard')} data-testid="menu-producer-dashboard">
                      <Settings className="w-4 h-4 mr-2" />
                      Dashboard Produttore
                    </DropdownMenuItem>
                  )}
                  {(user?.role === 'admin' || user?.role === 'super_admin') && (
                    <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="menu-admin">
                      <Settings className="w-4 h-4 mr-2" />
                      {user?.role === 'super_admin' ? 'Super Admin' : 'Amministrazione'}
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="menu-logout">
                    <LogOut className="w-4 h-4 mr-2" />
                    Esci
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex items-center gap-2">
                <Button variant="ghost" onClick={() => navigate('/login')} data-testid="login-btn">
                  Accedi
                </Button>
                <Button onClick={() => navigate('/registrati')} data-testid="register-btn">
                  Registrati
                </Button>
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="flex md:hidden items-center gap-2">
            <Link to="/carrello" className="relative p-2" data-testid="mobile-cart-link">
              <ShoppingCart className="w-5 h-5 text-text-secondary" />
              {itemCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-primary text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                  {itemCount}
                </span>
              )}
            </Link>
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="p-2" data-testid="mobile-menu-toggle">
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Search Bar */}
        {searchOpen && (
          <div className="py-4 border-t border-border">
            <form onSubmit={handleSearch} className="flex gap-2">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Cerca prodotti..."
                className="flex-1 px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                autoFocus
                data-testid="search-input"
              />
              <Button type="submit" data-testid="search-submit">Cerca</Button>
            </form>
          </div>
        )}

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-border">
            <nav className="flex flex-col gap-2">
              {/* Prodotti Section */}
              <div className="px-4 py-2">
                <Link 
                  to="/prodotti" 
                  className="block font-medium text-text-primary hover:text-primary mb-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Tutti i Prodotti
                </Link>
                <div className="ml-4 flex flex-col gap-1">
                  {MACRO_TYPES.map(macro => {
                    const Icon = macro.icon;
                    return (
                      <Link 
                        key={macro.id}
                        to={`/prodotti?macro_type=${macro.id}`} 
                        className="flex items-center gap-2 py-1.5 text-text-secondary hover:text-primary"
                        onClick={() => setMobileMenuOpen(false)}
                        data-testid={`mobile-nav-${macro.id.toLowerCase()}`}
                      >
                        <Icon className={`w-4 h-4 ${macro.color}`} />
                        {macro.label}
                      </Link>
                    );
                  })}
                </div>
              </div>
              <Link to="/produttori" className="px-4 py-2 hover:bg-background rounded-lg" onClick={() => setMobileMenuOpen(false)}>
                Produttori
              </Link>
              <Link to="/regioni" className="px-4 py-2 hover:bg-background rounded-lg" onClick={() => setMobileMenuOpen(false)}>
                Regioni
              </Link>
              {isAuthenticated ? (
                <>
                  <Link to="/account" className="px-4 py-2 hover:bg-background rounded-lg" onClick={() => setMobileMenuOpen(false)}>
                    Il mio account
                  </Link>
                  <Link to="/ordini" className="px-4 py-2 hover:bg-background rounded-lg" onClick={() => setMobileMenuOpen(false)}>
                    I miei ordini
                  </Link>
                  {user?.role === 'producer' && (
                    <Link to="/produttore/dashboard" className="px-4 py-2 hover:bg-background rounded-lg" onClick={() => setMobileMenuOpen(false)}>
                      Dashboard Produttore
                    </Link>
                  )}
                  <button onClick={handleLogout} className="px-4 py-2 text-left text-red-600 hover:bg-background rounded-lg">
                    Esci
                  </button>
                </>
              ) : (
                <div className="flex gap-2 px-4 pt-2">
                  <Button variant="outline" className="flex-1" onClick={() => { navigate('/login'); setMobileMenuOpen(false); }}>
                    Accedi
                  </Button>
                  <Button className="flex-1" onClick={() => { navigate('/registrati'); setMobileMenuOpen(false); }}>
                    Registrati
                  </Button>
                </div>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
